import React from "react"
import { Redirect, useHistory } from "react-router-dom"
import { LocationDetails } from "../types/Location"
import { PARENT_ID_MAPPING } from "./parentIdMapping"
import { Paths } from "#/utils/Paths"

export enum ServiceFormat {
  All = "",
  VirtualOnly = "virtual_only",
  InPerson = "in_person",
  InPersonAndVirtual = "in_person_and_virtual"
}

export const providerFinderRootPath = Paths.PROVIDER_FINDER

export const ProviderFinderParam = {
  Latitude: "lat",
  Longitude: "long",
  Country: "country",
  State: "state",
  Search: "search",
  Page: "page",
  RadiusInKm: "radiusInKm",
  CarrotPartner: "carrotPartner",
  ServiceFormat: "serviceFormat",
  ProviderType: "providerType",
  Keyword: "keyword",
  ProviderId: "providerId",
  ProviderNetworkId: "providerNetworkId",
  DoulaServices: "doulaServices",
  CarrotCenterOfExcellence: "carrotCenterOfExcellence",
  AcceptsDirectPay: "acceptsDirectPay"
} as const

type ProviderFinderParams = {
  [ProviderFinderParam.Latitude]?: string
  [ProviderFinderParam.Longitude]?: string
  [ProviderFinderParam.Country]?: string
  [ProviderFinderParam.State]?: string
  [ProviderFinderParam.Search]: string
  [ProviderFinderParam.Page]: number
  [ProviderFinderParam.RadiusInKm]?: number
  [ProviderFinderParam.CarrotPartner]?: boolean
  [ProviderFinderParam.ServiceFormat]?: ServiceFormat
  [ProviderFinderParam.ProviderType]?: string | string[]
  [ProviderFinderParam.Keyword]?: string
  [ProviderFinderParam.ProviderId]?: string
  [ProviderFinderParam.ProviderNetworkId]?: string
  [ProviderFinderParam.DoulaServices]?: string
  [ProviderFinderParam.CarrotCenterOfExcellence]?: boolean
  [ProviderFinderParam.AcceptsDirectPay]?: boolean
}

function buildProviderFinderSearchURL(params: ProviderFinderParams): URL {
  const newURL = new URL(`${providerFinderRootPath}/providers`, window.location.href)

  params.lat && newURL.searchParams.set(ProviderFinderParam.Latitude, params.lat)
  params.long && newURL.searchParams.set(ProviderFinderParam.Longitude, params.long)
  params.country && newURL.searchParams.set(ProviderFinderParam.Country, params.country)
  params.state && newURL.searchParams.set(ProviderFinderParam.State, params.state)
  newURL.searchParams.set(ProviderFinderParam.Search, params.search)
  newURL.searchParams.set(ProviderFinderParam.Page, params.page?.toString())

  if (params.keyword) {
    newURL.searchParams.set(ProviderFinderParam.Keyword, params.keyword)
  }

  if (params.providerId) {
    newURL.searchParams.set(ProviderFinderParam.ProviderId, params.providerId)
  }

  if (params.carrotPartner) {
    newURL.searchParams.set(ProviderFinderParam.CarrotPartner, params.carrotPartner.toString())
  }

  if (params.serviceFormat) {
    newURL.searchParams.set(ProviderFinderParam.ServiceFormat, params.serviceFormat)
  }

  if (params.carrotCenterOfExcellence) {
    newURL.searchParams.set(ProviderFinderParam.CarrotCenterOfExcellence, params.carrotCenterOfExcellence.toString())
  }

  if (params.acceptsDirectPay) {
    newURL.searchParams.set(ProviderFinderParam.AcceptsDirectPay, params.acceptsDirectPay.toString())
  }

  if (params.radiusInKm) {
    newURL.searchParams.set(ProviderFinderParam.RadiusInKm, params.radiusInKm.toFixed(1))
  }

  if (params.providerType && params.providerType.length) {
    const parsedProviderTypeString =
      typeof params.providerType === "string"
        ? params.providerType
        : params.providerType.filter((parentId) => Object.keys(PARENT_ID_MAPPING).indexOf(parentId) >= 0).join(",")
    newURL.searchParams.set(ProviderFinderParam.ProviderType, parsedProviderTypeString)
  }

  if (params.providerNetworkId) {
    newURL.searchParams.set(ProviderFinderParam.ProviderNetworkId, params.providerNetworkId)
  }

  if (params.doulaServices) {
    newURL.searchParams.set(ProviderFinderParam.DoulaServices, params.doulaServices)
  }
  return newURL
}

export function usePushUpdatedSearchParams() {
  const history = useHistory()

  return (updateSearchParams: (searchParamsToUpdate: URLSearchParams) => void) => {
    const searchParams = new URLSearchParams(history.location.search)
    updateSearchParams(searchParams)
    searchParams.set(ProviderFinderParam.Page, "1")
    history.location.search = searchParams.toString()
    history.push(history.location)
  }
}

export function useStringSearchParam(searchParam: keyof ProviderFinderParams): [string, (value: string) => void] {
  const history = useHistory()
  const pushUpdatedSearchParams = usePushUpdatedSearchParams()

  const currentSearchParams = new URLSearchParams(history.location.search)
  const searchParamValue = currentSearchParams.get(searchParam)

  const setSearchParam = (value: string) => {
    pushUpdatedSearchParams((searchParamsToUpdate: URLSearchParams) => {
      if (value) {
        searchParamsToUpdate.set(searchParam, value)
      } else {
        searchParamsToUpdate.delete(searchParam)
      }
    })
  }

  return [searchParamValue, setSearchParam]
}

const BOOLEAN_SEARCH_PARAM_TRUE_VALUE = "true"

export function useBooleanSearchParam(searchParam: keyof ProviderFinderParams): [boolean, (value: boolean) => void] {
  const history = useHistory()
  const pushUpdatedSearchParams = usePushUpdatedSearchParams()

  const currentSearchParams = new URLSearchParams(history.location.search)
  const searchParamValue =
    currentSearchParams.has(searchParam) && currentSearchParams.get(searchParam) === BOOLEAN_SEARCH_PARAM_TRUE_VALUE

  const setSearchParam = (value: boolean) => {
    pushUpdatedSearchParams((searchParamsToUpdate: URLSearchParams) => {
      if (value) {
        searchParamsToUpdate.set(searchParam, BOOLEAN_SEARCH_PARAM_TRUE_VALUE)
      } else {
        searchParamsToUpdate.delete(searchParam)
      }
    })
  }

  return [searchParamValue, setSearchParam]
}

export function useDoulaServicesSearchParam(): [string[], (doulaServices: string[]) => void] {
  const history = useHistory()
  const pushUpdatedSearchParams = usePushUpdatedSearchParams()
  const currentSearchParams = new URLSearchParams(history.location.search)

  const doulaServices = currentSearchParams.has(ProviderFinderParam.DoulaServices)
    ? currentSearchParams.get(ProviderFinderParam.DoulaServices).split(",").filter(Boolean)
    : []

  const setDoulaServicesSearchParam = (doulaServices: string[]) => {
    pushUpdatedSearchParams((searchParamsToUpdate: URLSearchParams) => {
      if (doulaServices?.length) {
        searchParamsToUpdate.set(ProviderFinderParam.DoulaServices, doulaServices.join(","))
      } else {
        searchParamsToUpdate.delete(ProviderFinderParam.DoulaServices)
      }
    })
  }

  return [doulaServices, setDoulaServicesSearchParam]
}

export function getServiceFormatSearched(params: URLSearchParams): ServiceFormat {
  const serviceFormatParam = params.get(ProviderFinderParam.ServiceFormat)
  return (Object.values(ServiceFormat) as string[]).includes(serviceFormatParam)
    ? (serviceFormatParam as ServiceFormat)
    : ServiceFormat.All
}

type getProviderFinderURLSearchParams = {
  searchLocation?: string
  locationDetails?: LocationDetails
  radiusInKm?: number | string
  companyCountryCode?: string
  carrotPartner?: boolean
  serviceFormat?: ServiceFormat
  providerType?: string | string[]
  keyword?: string
  providerId?: string
  providerNetworkId?: string
  doulaServices?: string
  carrotCenterOfExcellence?: boolean
  acceptsDirectPay?: boolean
}

export const getProviderFinderSearchURL = ({
  searchLocation,
  radiusInKm,
  locationDetails,
  carrotPartner,
  serviceFormat,
  providerType,
  keyword,
  providerId,
  providerNetworkId,
  doulaServices,
  carrotCenterOfExcellence,
  acceptsDirectPay
}: getProviderFinderURLSearchParams): URL => {
  const params: ProviderFinderParams = {
    [ProviderFinderParam.Search]: searchLocation,
    [ProviderFinderParam.Page]: 1,
    [ProviderFinderParam.Country]: locationDetails?.countryCode,
    [ProviderFinderParam.State]: locationDetails?.administrativeLevels?.level1short,
    [ProviderFinderParam.CarrotPartner]: carrotPartner,
    [ProviderFinderParam.ServiceFormat]: serviceFormat,
    [ProviderFinderParam.ProviderType]: providerType,
    [ProviderFinderParam.RadiusInKm]: Number(radiusInKm),
    [ProviderFinderParam.Keyword]: keyword,
    [ProviderFinderParam.ProviderId]: providerId,
    [ProviderFinderParam.ProviderNetworkId]: providerNetworkId,
    [ProviderFinderParam.DoulaServices]: doulaServices,
    [ProviderFinderParam.CarrotCenterOfExcellence]: carrotCenterOfExcellence,
    [ProviderFinderParam.AcceptsDirectPay]: acceptsDirectPay
  }
  params[ProviderFinderParam.Latitude] = locationDetails?.latitude && locationDetails.latitude.toString()
  params[ProviderFinderParam.Longitude] = locationDetails?.longitude && locationDetails.longitude.toString()

  return buildProviderFinderSearchURL(params)
}

const SIDEBAR_FILTERS_LIST = [
  ProviderFinderParam.CarrotPartner,
  ProviderFinderParam.ServiceFormat,
  ProviderFinderParam.CarrotCenterOfExcellence,
  ProviderFinderParam.AcceptsDirectPay,
  ProviderFinderParam.ProviderNetworkId,
  ProviderFinderParam.DoulaServices
] as const

export function getActiveFiltersCount(params: URLSearchParams): string[] {
  return SIDEBAR_FILTERS_LIST.filter((param) => {
    if (param === ProviderFinderParam.ServiceFormat) {
      const value = params.get(param)
      return !!value
    }
    return params.get(param) === "true"
  })
}

export const RedirectToProviderFinderURL = ({ url }: { url: URL }): JSX.Element => {
  if (!url) {
    return null
  }

  return <Redirect push to={{ pathname: url.pathname, search: url.search }} />
}
